/**
 * @module Shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */
// We need to enable type `any` because the State Store service can provide `any` value to the getState.
// any for watchedState bcoz watchstate returns void whereas unsibscribe is ISubscription type

import {
  Directive,
  ElementRef,
  OnInit,
  Renderer2,
  OnDestroy,
  Output,
  Input,
  EventEmitter,
} from '@angular/core';
import { Router } from '@angular/router';
import { PROFILE_PERMISSION_CONSTANTS } from '../../../../root/shared/constants/profile-permission.constants';
import { StateStoreService } from '@support/state-object-hash-utilities/services/state-store.service';
import * as objectPath from 'object-path';

@Directive({
  selector: '[tmoUIPermission]',
})
export class UIPermissionDirective implements OnInit, OnDestroy {
  @Input() public targetName: string;
  @Input() public defaultState: string;
  @Input() public set byPassPermission(value: boolean) {
    if (value) {
      this.byPassPermissions();
    }
  }

  @Input() public permissionType: string;
  @Output() public minMaxPermissionError: EventEmitter<Object>;

  private tgt: string;
  private routeName: string;
  private blurListener: Function;
  private initialTabIndex: number;

  public ngOnInit(): void {
    this.routeName = this.route.url;
    this.tgt = this.targetName;
    const isGlobal =
      this.tgt && this.tgt.charAt(0) === PROFILE_PERMISSION_CONSTANTS.GLOBAL_IDENTIFIER;
    this.routeName = (isGlobal && PROFILE_PERMISSION_CONSTANTS.GLOBAL_TARGET) || this.route.url;
    this.tgt = (isGlobal && this.tgt.substr(1)) || this.tgt;
    this.defaultState = this.defaultState ? this.defaultState : '';
    const permissionObject = this.stateStoreService.getState('app.permissionObject');
    const hasPermission = objectPath.has(
      permissionObject,
      this.routeName + '.elements.' + this.tgt
    );
    this.isPermissionEnabled(hasPermission);
    this.addPermissionChangeListener();
  }

  constructor(
    private element: ElementRef,
    private renderer: Renderer2,
    private route: Router,
    private stateStoreService: StateStoreService
  ) {
    this.minMaxPermissionError = new EventEmitter();
  }

  public ngOnDestroy(): void {
    if (this.blurListener) {
      this.blurListener(); // destroy blur event listener
      this.blurListener = null;
    }
  }

  private addPermissionChangeListener(): void {
    const listenerFunction = this.stateStoreService.getListener('app.permissionObject');
    if (listenerFunction) {
      this.stateStoreService.addListener(
        'app.permissionObject',
        (path: string, value: any): void => {
          listenerFunction(path, value);
          this.listenPermissionChange(path, value);
        }
      );
    } else {
      this.stateStoreService.addListener('app.permissionObject', this.listenPermissionChange);
    }
  }

  private listenPermissionChange = (path: any, value: any): void => {
    if (objectPath.has(value, this.routeName + '.elements.' + this.tgt)) {
      this.link(value[this.routeName]);
    } else {
      this.byPassPermissions();
      this.renderer.addClass(this.element.nativeElement, 'permissioned');
      this.validatepermissions(this.defaultState);
    }
  };

  private link(permissionObject: RouteNamePermissionObject): void {
    let classModifiers: string = this.defaultState;
    const objectType: string = 'object';
    const elements = permissionObject.elements;
    if (typeof elements[this.tgt] === objectType) {
      classModifiers = elements[this.tgt].identifier
        ? elements[this.tgt].identifier
        : classModifiers;
      if (elements[this.tgt].max || elements[this.tgt].min) {
        this.setBlurEvtListenerForMinMax(elements[this.tgt].max, elements[this.tgt].min);
      }
    } else {
      classModifiers = elements[this.tgt];
    }
    this.renderer.addClass(this.element.nativeElement, 'permissioned');
    this.validatepermissions(classModifiers);
  }

  /**
   * Set the Blur evt handler to check the range according for the min max value
   * @returns void
   */
  private setBlurEvtListenerForMinMax(max: string, min: string): void {
    if (this.blurListener) {
      return; // remove listener if exist
    }
    let targetElement = this.element.nativeElement;
    if (targetElement.tagName.toUpperCase() !== 'INPUT') {
      targetElement = this.renderer.selectRootElement('[targetName="' + this.tgt + '"] input');
    }
    this.blurListener = this.renderer.listen(targetElement, 'blur', (event: object) => {
      let maxLimit;
      let minLimit;
      let value;
      const maxType = 'MAX';
      const minType = 'MIN';
      maxLimit = parseFloat(max);
      minLimit = parseFloat(min);
      value = (isNaN(parseFloat(targetElement.value)) && -1) || parseFloat(targetElement.value);

      if (value > maxLimit) {
        this.minMaxPermissionError.emit({ type: maxType });
      }
      if (value < minLimit) {
        this.minMaxPermissionError.emit({ type: minType });
      }
    });
  }

  public validatepermissions(classModifiers: string): void {
    this.setInitialTabIndex();
    switch (classModifiers.toLowerCase()) {
      case 'editable':
        this.editableElement();
        break;
      case 'enable':
        this.enableElement();
        break;
      case 'readonly':
        this.readOnly();
        break;
      case 'disable':
        this.disableElement();
        break;
      case 'show':
        this.showElement();
        break;
      case 'hide':
        this.hideElement();
        break;
      default:
        this.hideElement();
    }
  }

  private hideElement(): void {
    this.renderer.addClass(this.element.nativeElement, 'hidden');
  }

  private showElement(): void {
    this.renderer.removeClass(this.element.nativeElement, 'hidden');
  }

  private disableElement(): void {
    this.showElement();
    if (
      (!this.element.nativeElement.getAttribute('disabled') &&
        !this.element.nativeElement.getAttribute('ng-disabled')) ||
      this.element.nativeElement.getAttribute('ng-disabled') !== 'true'
    ) {
      this.element.nativeElement.setAttribute('ng-disabled', true);
      this.element.nativeElement.setAttribute('disabled', 'disabled');
    }
    this.renderer.addClass(this.element.nativeElement, 'disabled');
    this.initialTabIndex = this.element.nativeElement.getAttribute('tabIndex');
    this.element.nativeElement.setAttribute('tabIndex', -1);
  }

  private enableElement(): void {
    this.showElement();
    if (this.element.nativeElement.getAttribute('disabled')) {
      this.element.nativeElement.removeAttribute('disabled');
    }
    if (this.element.nativeElement.getAttribute('ng-disabled') === 'true') {
      this.element.nativeElement.setAttribute('ng-disabled', false);
    }
    this.renderer.removeClass(this.element.nativeElement, 'disabled');
  }

  private editableElement(): void {
    this.showElement();
    this.element.nativeElement.removeAttribute('readonly');
    if (this.element.nativeElement.getAttribute('disabled')) {
      this.element.nativeElement.removeAttribute('disabled');
    }
    if (this.element.nativeElement.getAttribute('ng-disabled') === 'true') {
      this.element.nativeElement.setAttribute('ng-disabled', false);
    }
  }

  private readOnly(): void {
    this.showElement();
    this.element.nativeElement.setAttribute('readonly', true);
  }

  private byPassPermissions(): void {
    this.renderer.removeClass(this.element.nativeElement, 'permissioned');
    this.renderer.removeClass(this.element.nativeElement, 'hidden');
    this.renderer.removeClass(this.element.nativeElement, 'disabled');
    this.element.nativeElement.removeAttribute('ng-disabled');
    this.element.nativeElement.removeAttribute('disabled');
    this.element.nativeElement.removeAttribute('readonly');
    if (this.blurListener) {
      this.blurListener();
      this.blurListener = null;
    }
  }

  private setInitialTabIndex(): void {
    this.initialTabIndex = this.initialTabIndex || 0;
    this.element.nativeElement.setAttribute('tabIndex', this.initialTabIndex);
    this.initialTabIndex = 0;
  }

  private isPermissionEnabled(hasPermission: boolean): void {
    const permissionObject = this.stateStoreService.getState('app.permissionObject');
    if (permissionObject && hasPermission) {
      // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB && this.permissionType === 'RepOnly'){
      if (this.permissionType === 'RepOnly') {
        this.validatepermissions('enable');
      } else {
        this.link(permissionObject[this.routeName]);
      }
    } else {
      this.renderer.addClass(this.element.nativeElement, 'permissioned');
      this.validatepermissions(this.defaultState);
    }
  }
}

export interface RouteNamePermissionObject {
  elements: object[];
}
